/* eslint-disable @typescript-eslint/ban-ts-comment */
import 'react-toastify/dist/ReactToastify.css';
import './styles/useAppMessages.scss';

import { Check, Info, Warning, WarningOctagon } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useState } from 'react';
import {
  toast,
  ToastContainer,
  TypeOptions,
  UpdateOptions,
  Zoom,
} from 'react-toastify';

const FADE_OUT_TIME_MS = {
  warning: 5000,
  info: 5000,
  error: 10_000,
  success: 2500,
} as const;

const ICON_SIZE = 24;
const LOCAL_KEY = 'appMsg';
const className = 'c-AppMessages';

/**
 *  AppMessages are objects with 3 parameters:
 *  1. type: (string required) must be one of ['error', 'success', 'informative', 'warning']
 *  2. msg: (string required ) Message to show to the user
 *  3. fadeOut: (number optional) duration in milliseconds before fading out the message (default 5000)
 *  4. id: (number optional) id of the message, used to update the message if it is already shown
 */

interface IMessage {
  type: TypeOptions;
  msg: string;
  fadeOut?: number;
  id?: number;
  link?: React.ReactNode;
}

enum MessageType {
  ERROR = 'error',
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
}

export function useAppMessages(t = (msg: string) => msg) {
  const [localState, updateState] = useState<IMessage[]>(
    JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]'),
  );

  const defaultToastOptions = {
    pauseOnHover: true,
    position: toast.POSITION.BOTTOM_CENTER,
    className,
    transition: Zoom,
  };

  const customToastCall = (
    type: TypeOptions,
    msg: string | React.ReactNode,
    options: UpdateOptions,
  ) => {
    // @ts-ignore // TODO
    if (toast.isActive(options?.toastId)) {
      // @ts-ignore // TODO
      toast.update(options.toastId, {
        ...options,
        render: msg,
        type,
      });
      return;
    }

    const toastCallerFunction = type => {
      if (type === 'warning') return 'warn';

      return type;
    };

    toast[toastCallerFunction(type)](msg, {
      ...options,
      autoClose: options.toastId ? false : options.autoClose,
    });
  };

  const buildMessage = (msg: string, link: React.ReactNode) => {
    if (!link) return msg;
    return (
      <div className={`${className}__message_container`}>
        <span>{msg}</span>
        {link}
      </div>
    );
  };

  useEffect(() => {
    if (!localState.length) return;
    for (const { type, msg, id, link } of localState) {
      const message = buildMessage(t(msg), link);
      switch (type) {
        case MessageType.ERROR:
          customToastCall(type, message, {
            ...defaultToastOptions,
            autoClose: FADE_OUT_TIME_MS.error,
            icon: <Warning size={ICON_SIZE} weight="fill" />,
            toastId: id,
          });
          break;
        case MessageType.SUCCESS:
          customToastCall(type, message, {
            ...defaultToastOptions,
            autoClose: FADE_OUT_TIME_MS.success,
            icon: <Check size={ICON_SIZE} weight="fill" />,
            toastId: id,
          });
          break;
        case MessageType.INFO:
          customToastCall(type, message, {
            ...defaultToastOptions,
            autoClose: FADE_OUT_TIME_MS.info,
            icon: <Info size={ICON_SIZE} weight="fill" />,
            toastId: id,
          });
          break;
        case MessageType.WARNING:
          customToastCall(type, message, {
            ...defaultToastOptions,
            autoClose: FADE_OUT_TIME_MS.warning,
            icon: <WarningOctagon size={ICON_SIZE} weight="fill" />,
            toastId: id,
          });
          break;
        default:
          break;
      }
    }
    localStorage.removeItem(LOCAL_KEY);
    // Drain the queue after flushing it to the toast system. This is the
    // intentional reset of a render-driven message queue, not derivable state.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    updateState([]);
  }, [localState]);

  // Memoised so the returned setter keeps a stable reference across renders.
  // Without this, consumers that list it in a useEffect dependency array loop
  // infinitely when an error is set (effect → setMessages → re-render → new
  // setter ref → effect re-fires), stacking endless error toasts.
  const setNewMessages = useCallback((...parameters) => {
    const newMessages = parameters.flat();
    if (!newMessages) return;
    if (!newMessages.length) return;
    if (!newMessages[0].type || !newMessages[0].msg) return;
    updateState(newMessages);
  }, []);

  return [setNewMessages];
}

useAppMessages.type = MessageType;

/**
 * Set messages outside of a react component LC using localStorage
 * @param {Array} messages - array of AppMessages
 **/
export const setLocalAppMsg = (...parameters) => {
  const messages = parameters.flat();
  const isMsgInQueue = Boolean(localStorage.getItem(LOCAL_KEY));
  if (!messages.length) return;
  if (!messages[0].type || !messages[0].msg) return;
  if (isMsgInQueue) return;

  localStorage.setItem(LOCAL_KEY, JSON.stringify(messages));
};

export const AppMessagesContainer = () => <ToastContainer theme="dark" />;
